import React from 'react';
import {
  appSections,
  featureGroups,
  featureList,
  homeHighlights,
  iosDetails,
  premiumFeatures,
  workflow
} from '../content/siteContent';

const heroBadges = [
  'Music Visualizer',
  'Audio Spectrum',
  'Visualizer Templates',
  'VIZ Presets'
];

const heroStats = [
  {
    label: 'Visualizer Looks',
    value: 'Multi-style',
    detail: 'Spectrum, wave, particles, artwork, and more.'
  },
  {
    label: 'Workflow',
    value: 'Real-Time',
    detail: 'Preview edits while music keeps moving.'
  },
  {
    label: 'Output',
    value: 'Export Ready',
    detail: 'Cleaner rendering and stronger premium export flow.'
  }
];

const searchTerms = [
  'Music visualizer for iPhone',
  'Audio spectrum visualizer',
  'Spectrum visualizer templates',
  'Music visualizer templates',
  'VIZ preset import',
  'Custom visualizer presets',
  'Music video visualizer app',
  'Avee Player-style visualizer workflow'
];

const aveeTerms = [
  'Avee Player templates',
  'Avee Player visualizer',
  'Avee Player spectrum templates',
  'Avee Player music visualizer',
  'Avee visualizer preset',
  'Avee template import',
  'Avee equalizer and sleep timer',
  'Avee playlist and queue'
];

const Home: React.FC = () => {
  return (
    <div className="pb-10">
      <section className="relative overflow-hidden px-4 pb-12 pt-10 sm:px-6 md:pt-16">
        <div className="hero-orb left-[-8%] top-8 h-56 w-56 bg-brand-500/30"></div>
        <div className="hero-orb right-[4%] top-12 h-48 w-48 bg-cyanGlow/20"></div>
        <div className="mx-auto grid w-full max-w-6xl gap-10 lg:grid-cols-[1.02fr_0.98fr] lg:items-center">
          <div className="relative space-y-6">
            <div className="section-kicker transition duration-300 hover:border-white/20 hover:bg-white/10">
              Vizo for iPhone Music Visualizer Fans
            </div>
            <div className="flex max-w-2xl flex-wrap gap-2">
              {heroBadges.map((badge) => (
                <span key={badge} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium uppercase tracking-[0.22em] text-slate-300">
                  {badge}
                </span>
              ))}
            </div>
            <h1 className="hero-heading max-w-5xl font-semibold text-white">
              Music visualizer
              <span className="block text-white">
                meets audio spectrum
              </span>
              <span className="block bg-gradient-to-r from-white via-cyanGlow to-brand-500 bg-clip-text text-transparent">
                motion and templates.
              </span>
            </h1>
            <p className="max-w-2xl text-base leading-8 text-slate-300 sm:text-xl">
              Vizo is a music visualizer and audio spectrum visualizer for iPhone with real-time playback,
              visualizer templates, spectrum-style presets, built-in editing, library and queue tools, and clean export controls for short-form content.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <a
                href="https://apps.apple.com/tr/app/unvis-music-visualizer/id6749325448"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-brand-500 to-cyanGlow px-5 py-3 text-sm font-semibold text-white shadow-glow transition hover:-translate-y-0.5"
              >
                Download on the App Store
              </a>
              <a
                href="/support"
                className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm font-semibold text-white transition hover:border-white/20 hover:bg-white/10"
              >
                View Support
              </a>
            </div>
            <div className="editorial-card max-w-2xl p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-slate-400">App Store SEO Line</p>
                  <p className="mt-2 font-display text-xl font-semibold text-white">Vizo - Music & Audio Spectrum - Visualizer</p>
                </div>
                <span className="rounded-full border border-cyanGlow/30 bg-cyanGlow/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyanGlow">
                  iPhone App
                </span>
              </div>
              <p className="mt-3 max-w-xl text-sm leading-7 text-slate-300">
                Subtitle direction: music visualizer, spectrum presets, visualizer templates, and clean export workflows for iPhone.
              </p>
            </div>
            <div className="grid max-w-3xl gap-3 pt-2 sm:grid-cols-3">
              {heroStats.map((stat) => (
                <div key={stat.label} className="metric-card">
                  <p className="text-xs uppercase tracking-[0.24em] text-slate-400">{stat.label}</p>
                  <p className="mt-2 font-display text-2xl font-semibold text-white">{stat.value}</p>
                  <p className="mt-1 text-sm leading-6 text-slate-300">{stat.detail}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 -z-10 animate-pulseGlow bg-[radial-gradient(circle_at_top,rgba(124,92,255,0.35),transparent_50%)] blur-3xl"></div>
            <div className="device-stage animate-float p-4 sm:p-5">
              <div className="flex items-center justify-between rounded-[24px] border border-white/10 bg-white/5 px-4 py-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-slate-400">Creative Preview</p>
                  <p className="mt-1 text-sm font-medium text-white">Music visualizer scene preview</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-400"></span>
                  <span className="text-xs uppercase tracking-[0.22em] text-slate-400">Live</span>
                </div>
              </div>
              <div className="mt-4 overflow-hidden rounded-[30px] border border-white/10 bg-slate-950/60 p-2">
                <img
                  src="/real-app-screenshot.webp"
                  alt="Vizo music visualizer and audio spectrum visualizer preview"
                  className="w-full rounded-[22px]"
                />
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-[1.05fr_0.95fr]">
                <div className="premium-card p-4">
                  <p className="text-xs uppercase tracking-[0.22em] text-slate-400">Scene Flow</p>
                  <p className="mt-2 font-display text-2xl font-semibold text-white">Templates, presets, editor, export.</p>
                  <p className="mt-2 text-sm leading-6 text-slate-300">A stronger visual hierarchy for music-driven content creation.</p>
                </div>
                <div className="premium-card p-4">
                  <p className="text-xs uppercase tracking-[0.22em] text-slate-400">Why It Feels Better</p>
                  <ul className="mt-3 space-y-2 text-sm leading-6 text-slate-300">
                    <li className="flex gap-3"><span className="list-dot"></span><span>Cleaner section contrast</span></li>
                    <li className="flex gap-3"><span className="list-dot"></span><span>Richer bento-style cards</span></li>
                    <li className="flex gap-3"><span className="list-dot"></span><span>More premium motion cues</span></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 pb-4 sm:px-6">
        <div className="mx-auto grid w-full max-w-6xl gap-4 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="editorial-card p-6 sm:p-7">
            <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Product Focus</p>
            <div className="mt-5 grid gap-4 md:grid-cols-3">
              <div>
                <span className="block text-xl font-semibold text-white">Real Time</span>
                <span className="mt-2 block text-sm leading-7 text-slate-400">Music-reactive playback and scene updates</span>
              </div>
              <div>
                <span className="block text-xl font-semibold text-white">Pro Ready</span>
                <span className="mt-2 block text-sm leading-7 text-slate-400">Premium presets, export options, and restore flow</span>
              </div>
              <div>
                <span className="block text-xl font-semibold text-white">Clean Export</span>
                <span className="mt-2 block text-sm leading-7 text-slate-400">Improved rendering consistency and better output control</span>
              </div>
            </div>
          </div>
          <div className="premium-card p-6">
            <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Made For</p>
            <p className="mt-3 font-display text-2xl font-semibold text-white">Reels, edits, template packs, and music-driven clips.</p>
            <p className="mt-3 text-sm leading-7 text-slate-300">The landing page now reads more like a premium product showcase instead of a basic feature dump.</p>
          </div>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6">
        <div className="mx-auto w-full max-w-6xl">
          <div className="mx-auto mb-8 max-w-3xl text-center">
            <p className="section-kicker">
              Core Features
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              Built for music visualizer projects that feel faster, sharper, and easier to share.
            </h2>
            <p className="mt-4 text-base leading-8 text-slate-300">
              The current Vizo app focuses on cleaner music visualization, stronger spectrum preset management,
              premium visualizer templates, and more reliable export behavior.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {homeHighlights.map((item) => (
              <article className="premium-card p-6" key={item.title}>
                <h3 className="text-xl font-semibold text-white">{item.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-400">{item.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-6 sm:px-6">
        <div className="mx-auto mb-4 w-full max-w-6xl">
          <div className="editorial-card p-6 sm:p-7">
            <div className="max-w-3xl">
              <p className="section-kicker">
                Everything Included
              </p>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                All core Vizo features, listed clearly.
              </h2>
            </div>
            <div className="mt-8 grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
              <div className="grid gap-3 md:grid-cols-2">
              {featureList.map((feature) => (
                <div
                  className="chip-cloud-item flex gap-3"
                  key={feature}
                >
                  <span className="list-dot"></span>
                  <span className="text-sm leading-7 text-slate-300">{feature}</span>
                </div>
              ))}
              </div>
              <aside className="premium-card p-5">
                <p className="text-xs uppercase tracking-[0.24em] text-slate-400">Feature Snapshot</p>
                <h3 className="mt-3 text-2xl font-semibold text-white">A better bento rhythm for product scanning.</h3>
                <p className="mt-3 text-sm leading-7 text-slate-300">
                  Visitors now get a faster overview of Vizo through grouped feature cards, stronger contrast, and more editorial spacing.
                </p>
                <div className="mt-5 grid gap-3">
                  {['Templates and presets', 'Music library and queue', 'Editor and VIZ imports', 'Export and Vizo Pro'].map((label) => (
                    <div key={label} className="rounded-2xl border border-white/10 bg-slate-950/35 px-4 py-3 text-sm font-medium text-white">
                      {label}
                    </div>
                  ))}
                </div>
              </aside>
            </div>
          </div>
        </div>

        <div className="mx-auto grid w-full max-w-6xl gap-4 lg:grid-cols-2">
          <article className="premium-card p-6">
            <p className="section-kicker">
              How It Works
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-white">Create visuals in a faster flow.</h2>
            <ol className="mt-5 space-y-3 pl-5 text-sm leading-7 text-slate-300">
              {workflow.map((step) => (
                <li key={step} className="list-decimal">
                  {step}
                </li>
              ))}
            </ol>
          </article>

          <article className="premium-card p-6">
            <p className="section-kicker">
              Vizo Pro
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-white">
              Unlock premium tools when you need more control.
            </h2>
            <ul className="mt-5 space-y-3 text-sm leading-7 text-slate-300">
              {premiumFeatures.map((feature) => (
                <li key={feature} className="flex gap-3">
                  <span className="list-dot"></span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <a href="/support" className="mt-6 inline-flex text-sm font-semibold text-white transition hover:text-cyanGlow">
              Learn about support and subscription help
            </a>
          </article>
        </div>
      </section>

      <section className="px-4 py-6 sm:px-6">
        <div className="mx-auto w-full max-w-6xl">
          <div className="mb-6 max-w-3xl">
            <p className="section-kicker">
              Search Friendly
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              Looking for a music visualizer, spectrum visualizer, or template-based editor?
            </h2>
            <p className="mt-4 text-sm leading-7 text-slate-300">
              Vizo is built for people searching for a music visualizer app, an audio spectrum visualizer, visualizer templates,
              spectrum-style presets, and customizable iPhone music video workflows.
            </p>
          </div>
          <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
            <article className="premium-card p-6">
              <h3 className="text-2xl font-semibold text-white">Useful search matches</h3>
              <ul className="mt-5 grid gap-3 md:grid-cols-2">
                {searchTerms.map((term) => (
                  <li key={term} className="chip-cloud-item">
                    {term}
                  </li>
                ))}
              </ul>
            </article>
            <article className="premium-card p-6">
              <h3 className="text-2xl font-semibold text-white">About Avee Player searches</h3>
              <p className="mt-4 text-sm leading-7 text-slate-300">
                Vizo is an independent iPhone app, not an official Avee Player product. But this site now clearly explains Vizo for people
                searching for Avee Player-style templates, music visualizer presets, and spectrum-based visualizer workflows.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="px-4 py-6 sm:px-6">
        <div className="mx-auto w-full max-w-6xl">
          <div className="mb-6 max-w-3xl">
            <p className="section-kicker">
              Avee Player Search Intent
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              What Avee Player users usually look for, and why Vizo is relevant.
            </h2>
            <p className="mt-4 text-sm leading-7 text-slate-300">
              Many users searching for Avee Player are looking for music visualizer templates, spectrum visualizer presets,
              playlist and queue tools, equalizer controls, sleep timer support, image or GIF-based visuals, and export-ready music video workflows.
            </p>
          </div>
          <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
            <article className="premium-card p-6">
              <h3 className="text-2xl font-semibold text-white">Popular Avee Player terms</h3>
              <ul className="mt-5 grid gap-3 md:grid-cols-2">
                {aveeTerms.map((term) => (
                  <li key={term} className="chip-cloud-item">
                    {term}
                  </li>
                ))}
              </ul>
            </article>
            <article className="premium-card p-6">
              <h3 className="text-2xl font-semibold text-white">Why Vizo matches that search</h3>
              <ul className="mt-5 space-y-3 text-sm leading-7 text-slate-300">
                <li className="flex gap-3"><span className="list-dot"></span><span>Visualizer templates and spectrum-style presets</span></li>
                <li className="flex gap-3"><span className="list-dot"></span><span>Queue, saved lists, search, sort, and library browsing</span></li>
                <li className="flex gap-3"><span className="list-dot"></span><span>Equalizer, sleep timer, and playback-related controls</span></li>
                <li className="flex gap-3"><span className="list-dot"></span><span>Photos, Files, VIZ imports, editing tools, and export workflow</span></li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section className="px-4 py-6 sm:px-6">
        <div className="mx-auto w-full max-w-6xl">
          <div className="mb-6 max-w-3xl">
            <p className="section-kicker">
              Feature Details
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              A clearer look at what you can actually do in Vizo.
            </h2>
            <p className="mt-4 text-sm leading-7 text-slate-300">
              This section focuses on product features and user-facing benefits only, so visitors can understand the app quickly.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {featureGroups.map((group) => (
              <article className="premium-card p-6" key={group.title}>
                <h3 className="text-2xl font-semibold text-white">{group.title}</h3>
                <p className="mt-3 text-sm leading-7 text-slate-300">{group.description}</p>
                <ul className="mt-5 space-y-3 text-sm leading-7 text-slate-300">
                  {group.items.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="list-dot"></span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-6 sm:px-6">
        <div className="mx-auto w-full max-w-6xl">
          <div className="mb-6 max-w-3xl">
            <p className="section-kicker">
              App Sections
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              The full Vizo experience, section by section.
            </h2>
            <p className="mt-4 text-sm leading-7 text-slate-300">
              This breakdown reflects the main parts of the app so users can quickly understand what is included.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {appSections.map((section, index) => (
              <article
                className={`premium-card p-6 ${index === 0 ? 'xl:col-span-2' : ''}`}
                key={section.title}
              >
                <h3 className="text-2xl font-semibold text-white">{section.title}</h3>
                <ul className="mt-5 space-y-3 text-sm leading-7 text-slate-300">
                  {section.items.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="list-dot"></span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-6 sm:px-6">
        <div className="mx-auto grid w-full max-w-6xl gap-4 lg:grid-cols-[0.95fr_1.05fr]">
          <article className="premium-card p-6">
            <p className="section-kicker">
              iPhone and iOS
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-white">Clear platform details for users and App Store visitors.</h2>
            <p className="mt-4 text-sm leading-7 text-slate-300">
              This section explains availability, product flow, and what users can expect from the current Vizo experience.
            </p>
          </article>

          <article className="premium-card p-6">
            <div className="grid gap-3 md:grid-cols-2">
              {iosDetails.map((detail) => (
                <div
                  className="chip-cloud-item"
                  key={detail}
                >
                  {detail}
                </div>
              ))}
            </div>
          </article>
        </div>
      </section>
    </div>
  );
};

export default Home;
